import type {
    ConditionalStringValueOption,
    DynamicStringValue,
    FieldCondition,
    FormDataMap,
    FormValues,
} from '../types/form-builder.types';
import { resolveCondition } from './resolve-condition.utils';

const collectConditionDependencies = <FormValuesType extends FormValues, FieldId extends string>(
    condition: FieldCondition<FormValuesType, FieldId>
): string[] => {
    switch (condition.type) {
        case 'when':
        case 'whenNot':
        case 'whenHasValue': {
            return [condition.field];
        }

        case 'whenAll':
        case 'whenSome': {
            return condition.conditions.flatMap((child) => collectConditionDependencies(child));
        }

        case 'custom': {
            return [...condition.dependsOn];
        }

        case 'whenData': {
            return [];
        }
    }
};

const isConditionalStringValueOptions = <FormValuesType extends FormValues, FieldId extends string>(
    value: DynamicStringValue<FormValuesType, FieldId>
): value is ReadonlyArray<ConditionalStringValueOption<FormValuesType, FieldId>> => Array.isArray(value);

export const resolveDynamicStringValue = <FormValuesType extends FormValues, FieldId extends string>({
    data,
    formValues,
    value,
}: {
    readonly data?: FormDataMap | undefined;
    readonly formValues: FormValuesType;
    readonly value: DynamicStringValue<FormValuesType, FieldId> | undefined;
}): string | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (!isConditionalStringValueOptions(value)) {
        return value;
    }

    return value.find((option) => resolveCondition(option.condition, formValues, data))?.value;
};

export const collectDynamicStringValueDependencies = <FormValuesType extends FormValues, FieldId extends string>(
    value: DynamicStringValue<FormValuesType, FieldId> | undefined
): string[] => {
    if (value === undefined || !isConditionalStringValueOptions(value)) {
        return [];
    }

    return value.flatMap((option) =>
        typeof option.condition === 'boolean' ? [] : collectConditionDependencies(option.condition)
    );
};
